"use client"

import { configureAxios } from "@/app/login/api";
import useAppSettings from "@/hooks/useAppSettings";
import useLabayhPlusSubscription from "@/hooks/useLabayhPlusSubscription";
import useRecentConsultants from "@/hooks/useRecentConsultants";
import useSignInToFirebase from "@/hooks/useSignInToFirebase";
import { firebaseConfig } from "@/utils";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { initializeApp } from "firebase/app";
import { Dispatch, SetStateAction, createContext, useContext, useEffect, useState } from "react";
import KeysComponent from "./KeysComponent";
import { User } from "./user.model";
import { usePathname } from "next/navigation";
import { sendGTMEvent } from "@next/third-parties/google";

const UserContext = createContext<{
  user: User | null,
  setUser: Dispatch<SetStateAction<User | null>>,
  isLabayhPlusSubscribed: boolean,
  labayhPlusData: any
  appSettings: any
  consultantsImages: { name: string, profile_image: string, profile_image_hash: string }[]
}>({
  user: null,
  setUser: () => { },
  isLabayhPlusSubscribed: false,
  labayhPlusData: null,
  appSettings: null,
  consultantsImages: []
});

const queryClient = new QueryClient();

const UserContextProvider = ({ children }: any) => {

  const [user, setUser] = useState<User | null>(null);
  const { data: appSettings } = useAppSettings();
  const { data: recentConsultants } = useRecentConsultants(!!user);
  const { data: labayhPlusData } = useLabayhPlusSubscription(!!user?.id);
  const [isAppConfigured, setIsAppConfigured] = useState(false);
  useSignInToFirebase(user?.id || "");
  const pathName = usePathname().slice(1);

  useEffect(() => {
    sendGTMEvent({ event: `view-${pathName || "home"}-page` });
  }, [pathName]);

  useEffect(() => {
    const accessToken = localStorage?.getItem("@accessToken") || "";
    configureAxios(accessToken);
    const _user = JSON.parse(localStorage?.getItem("@user") || "null")
    setUser(_user);
    initializeApp(firebaseConfig);
    setIsAppConfigured(true);
  }, []);

  if (!isAppConfigured) return null
  return <UserContext.Provider
    value={{
      user,
      setUser: value => {
        setUser(value);
        localStorage.setItem("@user", JSON.stringify(value));
      },
      isLabayhPlusSubscribed: !labayhPlusData ? false : labayhPlusData?.expired ? false : true,
      labayhPlusData,
      appSettings,
      consultantsImages: recentConsultants?.onlineConsultants || []
    }
    }>
    <KeysComponent />
    {children}
  </UserContext.Provider>
}

export const useUserContext = () => useContext(UserContext);

const UserContextProviderWrapper = ({ children }: any) => {
  return <QueryClientProvider client={queryClient}>
    <UserContextProvider>
      {children}
    </UserContextProvider>
  </QueryClientProvider>
}

export default UserContextProviderWrapper;